import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { 
  FileText, 
  CreditCard, 
  Ticket, 
  Users,
  ArrowRight,
  Check,
  AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getPlanById, getInvoicesByCompanyId, getTicketsByCompanyId } from '../../services/storage';

export const ClientDashboard = () => {
  const { user, company, subscription } = useAuth();
  
  const plan = subscription ? getPlanById(subscription.planId) : null;
  const invoices = company ? getInvoicesByCompanyId(company.id) : [];
  const tickets = company ? getTicketsByCompanyId(company.id) : [];

  const pendingInvoices = invoices.filter(i => i.status === 'pending').length;
  const openTickets = tickets.filter(t => t.status === 'open').length;

  return (
    <DashboardLayout>
      <Helmet>
        <title>Dashboard | Portal Ethics</title>
      </Helmet>

      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-primary-blue to-primary-darkBlue rounded-2xl p-8 text-white">
          <h1 className="text-3xl font-bold mb-2">
            Bem-vindo, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-white/80">
            Aqui está o resumo da sua conta e atividades recentes.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">Plano Atual</p>
                <p className="text-xl font-bold text-neutral-900 mt-1">{plan?.name || 'N/A'}</p>
              </div>
              <div className="w-12 h-12 bg-primary-blue/10 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary-blue" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">Próximo Vencimento</p>
                <p className="text-xl font-bold text-neutral-900 mt-1">
                  {subscription?.nextBillingDate 
                    ? new Date(subscription.nextBillingDate).toLocaleDateString('pt-BR')
                    : 'N/A'}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">Faturas Pendentes</p>
                <p className="text-xl font-bold text-neutral-900 mt-1">{pendingInvoices}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">Tickets Abertos</p>
                <p className="text-xl font-bold text-neutral-900 mt-1">{openTickets}</p>
              </div>
              <div className="w-12 h-12 bg-primary-orange/10 rounded-lg flex items-center justify-center">
                <Ticket className="w-6 h-6 text-primary-orange" />
              </div>
            </div>
          </Card>
        </div>

        {/* Contracted Tools */}
        <div>
          <h2 className="text-xl font-bold text-neutral-900 mb-4">Ferramentas Contratadas</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-primary-blue" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-neutral-900 mb-1">Código de Conduta</h3>
                  <p className="text-sm text-neutral-600 mb-3">
                    Gerencie e distribua o código de conduta da sua empresa.
                  </p>
                  <Link to="/dashboard/ferramentas/codigo-conduta" className="text-primary-blue text-sm font-medium hover:underline flex items-center gap-1">
                    Acessar <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Ticket className="w-6 h-6 text-primary-orange" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-neutral-900 mb-1">Canal de Denúncias</h3>
                  <p className="text-sm text-neutral-600 mb-3">
                    Acesse e gerencie as denúncias recebidas.
                  </p>
                  <Link to="/dashboard/ferramentas/denuncias" className="text-primary-blue text-sm font-medium hover:underline flex items-center gap-1">
                    Acessar <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-neutral-900 mb-1">Treinamentos</h3>
                  <p className="text-sm text-neutral-600 mb-3">
                    Acesse treinamentos e certificações.
                  </p>
                  <Link to="/dashboard/ferramentas/treinamentos" className="text-primary-blue text-sm font-medium hover:underline flex items-center gap-1">
                    Acessar <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Plan Info */}
        <Card className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                Plano {plan?.name}
              </h3>
              <p className="text-neutral-600">
                {plan?.features.slice(0, 3).map((feature, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 mr-4">
                    <Check className="w-4 h-4 text-success" />
                    {feature}
                  </span>
                ))}
              </p>
            </div>
            <Link to="/dashboard/assinatura">
              <Button variant="outline">
                Gerenciar Assinatura
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};
